import { isNotDefined } from '@sgme/fp';
import { Box, HStack, VStack } from '@sgme/ui';
import clsx from 'clsx';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { BlotterErrorIndication } from '@/App/Blotter/BlotterErrorIndication.tsx';
import { BlotterFilters } from '@/App/Blotter/filters/index.tsx';
import { BlotterAccumulatorGrid } from '@/App/Blotter/grids/accumulator/BlotterAccumulatorGrid.tsx';
import type { BlotterGridApi, BlotterGridComponent } from '@/App/Blotter/grids/BlotterGridComponent.tsx';
import { BlotterCashGrid } from '@/App/Blotter/grids/cash/BlotterCashGrid.tsx';
import { BlotterOptionGrid } from '@/App/Blotter/grids/option/BlotterOptionGrid.tsx';
import { BlotterOrderGrid } from '@/App/Blotter/grids/order/BlotterOrderGrid.tsx';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { blotterSlice } from '@/store/blotter/slice.ts';
import { type BlotterType, blotterTypes } from '@/store/blotter/types/index.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { ZINDEX_TILE__DRAGGED } from '@/utils/grid/zIndices.ts';

const MIN_HEIGHT_PX = 200;

const BlotterEntriesGrids: { [key in BlotterType]: BlotterGridComponent } = {
  Cash: BlotterCashGrid,
  Option: BlotterOptionGrid,
  Accumulator: BlotterAccumulatorGrid,
  Order: BlotterOrderGrid,
};

type Props = { onRequestToHide: () => void };

export const BlotterPanel = ({ onRequestToHide }: Props) => {
  const dispatch = useAppDispatch();
  const track = useTrackEvent();

  const panelRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<BlotterGridApi>(null);

  const savedPanelHeight = useAppSelector(blotterSlice.selectors.getPanelHeight);
  const activeTab = useAppSelector(blotterSlice.selectors.getActiveTab);

  // oxlint-disable-next-line exhaustive-deps -- handlers ref are always the same
  const engage = useCallback(() => {
    window.addEventListener('mousemove', handleMouseMove, { capture: true });
    window.addEventListener('mouseup', handleMouseUp, { capture: true });
  }, []);

  // oxlint-disable-next-line exhaustive-deps -- handlers ref are always the same
  const disengage = useCallback(() => {
    window.removeEventListener('mousemove', handleMouseMove, { capture: true });
    window.removeEventListener('mouseup', handleMouseUp, { capture: true });
  }, []);

  const handleMouseUp = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      disengage();
      if (panelRef.current) {
        dispatch(blotterSlice.actions.savePanelHeight(panelRef.current.clientHeight));
        track('Resize');
      }
    },
    [disengage],
  );

  const handleMouseMove = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const closestRelativeBoundingRect = panelRef.current?.closest('.position-relative')?.getBoundingClientRect();
    if (isNotDefined(closestRelativeBoundingRect)) {
      return;
    }
    const newHeight = closestRelativeBoundingRect.height - event.clientY + closestRelativeBoundingRect.top;

    if (newHeight >= MIN_HEIGHT_PX && newHeight <= closestRelativeBoundingRect.height - MIN_HEIGHT_PX) {
      panelRef.current?.style.setProperty('height', `${newHeight}px`);
    }
  }, []);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      engage();
    },
    [engage],
  );

  const handleClickOnTab = useCallback(
    (tabType: BlotterType) => () => {
      dispatch(blotterSlice.actions.setActiveTab(tabType));
      track('Switch tab', tabType);
    },
    [],
  );

  const handleClickOnExport = useCallback(() => {
    gridRef.current?.export();
    track('Download spreadsheet');
  }, []);

  useLayoutEffect(() => {
    panelRef.current?.style.setProperty('height', `${savedPanelHeight}px`);
  }, [savedPanelHeight]);

  const blotterStatus = useAppSelector(blotterSlice.selectors.getStatus);
  const counts = useAppSelector(blotterSlice.selectors.getCountPerType);

  const Grid = BlotterEntriesGrids[activeTab];

  return (
    <VStack
      // overflow-visible to allow the date range picker of the "historical filter" to show its selection panel
      className="w-100 bg-primary-alt overflow-visible border-top"
      style={{
        bottom: '0px',
        zIndex: ZINDEX_TILE__DRAGGED + 1, // to ensure that the blotter can be seen when a tile is zoomed
      }}
      ref={panelRef}
      data-e2e="blotter-panel"
    >
      <HStack
        horizontalAlignment="center"
        style={{ cursor: 'row-resize', height: '20px' }}
        onMouseDown={handleMouseDown}
      >
        <Box flex="min">
          <i className="icon icon-sm" style={{ cursor: 'row-resize', width: '19px' }}>
            drag_handler
          </i>
        </Box>
        <BlotterErrorIndication />
      </HStack>

      <Box
        flex="min"
        // overflow-visible to allow the date range picker of the "historical filter" to show its selection panel
        className="overflow-visible"
      >
        <ul className="nav nav-tabs border-primary mb-0 w-100 px-2 user-select-none">
          {blotterTypes.map((type) => (
            <li className="nav-item" key={type} data-e2e={`blotter-tab-${type}`.toLowerCase()}>
              <button
                type="button"
                className={clsx('nav-link', { active: activeTab === type })}
                onClick={handleClickOnTab(type)}
              >
                <FormattedMessage id={`blotter.tab.${type}`} values={{ count: counts[type] }} />
              </button>
            </li>
          ))}

          <li className="d-flex justify-content-end flex-grow-1 h-50 my-auto">
            <div className="btn-group">
              <BlotterFilters activeTab={activeTab} />

              <button
                type="button"
                className="btn btn-flat-primary btn-md p-0 me-2"
                onClick={handleClickOnExport}
                disabled={counts[activeTab] === 0}
              >
                <i className="icon icon-md">save_alt</i>
              </button>

              <button type="button" className="btn btn-flat-primary btn-md p-0" onClick={onRequestToHide}>
                <i className="icon icon-md">minimize</i>
              </button>
            </div>
          </li>
        </ul>
      </Box>

      <Box flex="expand">
        {(blotterStatus === 'unregistered' || blotterStatus === 'registering') && (
          <VStack verticalAlignment="center" horizontalAlignment="center" className="h-100">
            <Box className="spinner spinner-xl" />
          </VStack>
        )}
        {blotterStatus === 'registered' && <Grid ref={gridRef} />}
      </Box>
    </VStack>
  );
};
